import type { Manifest } from '../types';

const MAX_RECORDINGS = 10_000;
const MAX_ITEMS = 20_000;
const MAX_STRING = 4_096;
const SHA256 = /^sha256:[0-9a-f]{64}$/;

export interface ReleaseDescriptor {
  format: 'brahm-release/v1';
  releaseDigest: string;
  manifestDigest: string;
  manifest: { path: string; bytes: number; contentDigest: string };
  sourceManifestDigest: string;
}

export class ManifestValidationError extends Error {
  constructor(message: string) {
    super(`Invalid manifest: ${message}`);
    this.name = 'ManifestValidationError';
  }
}

function fail(path: string, message: string): never {
  throw new ManifestValidationError(`${path} ${message}`);
}

function object(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail(path, 'must be an object');
  return value as Record<string, unknown>;
}

function array(value: unknown, path: string, max = MAX_ITEMS): unknown[] {
  if (!Array.isArray(value)) fail(path, 'must be an array');
  if (value.length > max) fail(path, `has too many items (maximum ${max})`);
  return value;
}

function string(value: unknown, path: string, allowEmpty = false): string {
  if (typeof value !== 'string' || (!allowEmpty && value.length === 0) || value.length > MAX_STRING) {
    fail(path, `must be a ${allowEmpty ? '' : 'non-empty '}bounded string`);
  }
  return value;
}

function finite(value: unknown, path: string, min = 0): number {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < min) {
    fail(path, `must be a finite number at least ${min}`);
  }
  return value;
}

function integer(value: unknown, path: string, min = 0): number {
  const n = finite(value, path, min);
  if (!Number.isInteger(n)) fail(path, 'must be an integer');
  return n;
}

function enumValue(value: unknown, path: string, choices: readonly string[]): void {
  if (typeof value !== 'string' || !choices.includes(value)) fail(path, 'has an unsupported value');
}

function assetPath(value: unknown, path: string): void {
  const raw = string(value, path);
  let decoded: string;
  try { decoded = decodeURIComponent(raw); } catch { fail(path, 'has invalid escaping'); }
  if (decoded.startsWith('/') || /^[a-z][a-z0-9+.-]*:/i.test(decoded)
      || decoded.includes('\\') || decoded.includes('?') || decoded.includes('#')
      || /[\u0000-\u001f\u007f]/.test(decoded)
      || decoded.split('/').some((part) => part === '..' || part === '.')) {
    fail(path, 'must be a relative asset path without traversal');
  }
}

function files(value: unknown, path: string): void {
  const map = object(value, path);
  for (const codec of ['opus', 'webm', 'm4a']) {
    if (map[codec] === undefined) continue;
    const file = object(map[codec], `${path}.${codec}`);
    assetPath(file.path, `${path}.${codec}.path`);
    integer(file.bytes, `${path}.${codec}.bytes`);
  }
}

function acousticAnalysis(value: unknown, path: string): void {
  const analysis = object(value, path);
  if (integer(analysis.schemaVersion, `${path}.schemaVersion`, 1) !== 1) {
    fail(`${path}.schemaVersion`, 'is unsupported');
  }
  string(analysis.method, `${path}.method`);
  string(analysis.version, `${path}.version`);
  integer(analysis.frameRate, `${path}.frameRate`, 1);
  const total = integer(analysis.sourceFrameCount, `${path}.sourceFrameCount`);
  if (!SHA256.test(string(analysis.recipeDigest, `${path}.recipeDigest`))) {
    fail(`${path}.recipeDigest`, 'must be a SHA-256 digest');
  }
  const regions = array(analysis.regions, `${path}.regions`);
  if (total > 0 && regions.length === 0) fail(`${path}.regions`, 'must cover the source');
  let cursor = 0;
  regions.forEach((raw, i) => {
    const p = `${path}.regions[${i}]`;
    const region = object(raw, p);
    const start = integer(region.startFrame, `${p}.startFrame`);
    const end = integer(region.endFrame, `${p}.endFrame`);
    if (start !== cursor) fail(p, `must start at frame ${cursor} without a hole or overlap`);
    if (end <= start || end > total) fail(p, 'has invalid frame bounds');
    enumValue(region.classification, `${p}.classification`, ['voice', 'quiet', 'other', 'unknown']);
    if (region.confidence !== null) {
      const confidence = finite(region.confidence, `${p}.confidence`);
      if (confidence > 1) fail(`${p}.confidence`, 'must not exceed 1');
    }
    const evidence = object(region.evidence, `${p}.evidence`);
    string(evidence.method, `${p}.evidence.method`);
    string(evidence.version, `${p}.evidence.version`);
    if (evidence.reason !== undefined) string(evidence.reason, `${p}.evidence.reason`);
    cursor = end;
  });
  if (cursor !== total) fail(`${path}.regions`, `ends at frame ${cursor}, expected ${total}`);
}

function phaseVariants(value: unknown, path: string, speech: unknown[]): void {
  const phases = object(value, path);
  for (const phase of ['opening', 'closing'] as const) {
    if (phases[phase] === undefined) continue;
    array(phases[phase], `${path}.${phase}`).forEach((raw, i) => {
      const p = `${path}.${phase}[${i}]`;
      const variant = object(raw, p);
      string(variant.id, `${p}.id`);
      const island = integer(variant.island, `${p}.island`);
      if (island >= speech.length) fail(`${p}.island`, 'is outside the speech array');
      const start = finite(variant.srcStart, `${p}.srcStart`);
      const end = finite(variant.srcEnd, `${p}.srcEnd`);
      if (end <= start) fail(p, 'has invalid source bounds');
      const sourceIsland = object(speech[island], `${p}.islandSource`);
      if (start < Number(sourceIsland.srcStart) || end > Number(sourceIsland.srcEnd)) {
        fail(p, 'is outside its reviewed speech island');
      }
      if (sourceIsland.role !== phase) fail(`${p}.island`, `must reference the ${phase} island`);
      enumValue(variant.boundaryBasis, `${p}.boundaryBasis`, ['complete-thought']);
      const review = object(variant.review, `${p}.review`);
      enumValue(review.status, `${p}.review.status`, ['approved']);
      string(review.decisionId, `${p}.review.decisionId`);
    });
  }
}

function validateRecording(value: unknown, index: number): string {
  const path = `recordings[${index}]`;
  const rec = object(value, path);
  const id = string(rec.id, `${path}.id`);
  string(rec.title, `${path}.title`);
  string(rec.recordedOn, `${path}.recordedOn`);
  const nativeSec = finite(rec.nativeSec, `${path}.nativeSec`);
  const ratio = finite(rec.speechRatio, `${path}.speechRatio`);
  if (ratio > 1) fail(`${path}.speechRatio`, 'must not exceed 1');

  const speech = array(rec.speech, `${path}.speech`);
  speech.forEach((raw, i) => {
    const p = `${path}.speech[${i}]`;
    const island = object(raw, p);
    const start = finite(island.srcStart, `${p}.srcStart`);
    const end = finite(island.srcEnd, `${p}.srcEnd`);
    // Legacy native durations were rounded to centiseconds while segment endpoints kept
    // millisecond precision. The tolerance admits that representation loss only.
    if (end <= start || end > nativeSec + 0.01) fail(p, 'has source bounds outside the recording');
    enumValue(island.role, `${p}.role`, ['opening', 'reminder', 'closing']);
    if (island.byteStart !== undefined) integer(island.byteStart, `${p}.byteStart`);
    if (island.byteEnd !== undefined) integer(island.byteEnd, `${p}.byteEnd`);
    if (island.files !== undefined) files(island.files, `${p}.files`);
    if (island.parts !== undefined) array(island.parts, `${p}.parts`).forEach((rawPart, j) => {
      const pp = `${p}.parts[${j}]`;
      const part = object(rawPart, pp);
      const partStart = finite(part.srcStart, `${pp}.srcStart`);
      const partEnd = finite(part.srcEnd, `${pp}.srcEnd`);
      if (partEnd <= partStart || partStart < start || partEnd > end + 0.001) fail(pp, 'is outside its island');
      if (part.pcmTotalFrames !== undefined) integer(part.pcmTotalFrames, `${pp}.pcmTotalFrames`);
      if (part.files !== undefined) files(part.files, `${pp}.files`);
    });
  });

  array(rec.gaps, `${path}.gaps`).forEach((raw, i) => {
    const p = `${path}.gaps[${i}]`;
    const gap = object(raw, p);
    const after = integer(gap.afterIsland, `${p}.afterIsland`, -1);
    if (after >= speech.length) fail(`${p}.afterIsland`, 'is outside the speech array');
    finite(gap.nativeSec, `${p}.nativeSec`);
    const min = finite(gap.minSec, `${p}.minSec`);
    const max = finite(gap.maxSec, `${p}.maxSec`);
    if (max < min) fail(p, 'has maxSec below minSec');
    if (typeof gap.major !== 'boolean') fail(`${p}.major`, 'must be boolean');
    if (gap.events !== undefined) array(gap.events, `${p}.events`);
  });

  enumValue(rec.tone, `${path}.tone`, ['synth', 'inaudible', 'loop', 'fixed']);
  if (rec.toneProfile !== undefined && rec.toneProfile !== null) {
    assetPath(rec.toneProfile, `${path}.toneProfile`);
  }
  if (rec.audio !== undefined && rec.audio !== null) {
    const audio = object(rec.audio, `${path}.audio`);
    assetPath(audio.path, `${path}.audio.path`);
    integer(audio.bytes, `${path}.audio.bytes`);
    finite(audio.sampleRate, `${path}.audio.sampleRate`, 1);
    enumValue(audio.codec, `${path}.audio.codec`, ['opus']);
  }
  array(rec.themes ?? [], `${path}.themes`).forEach((item, i) => string(item, `${path}.themes[${i}]`));
  array(rec.stories ?? [], `${path}.stories`).forEach((item, i) => {
    const story = object(item, `${path}.stories[${i}]`);
    string(story.id, `${path}.stories[${i}].id`);
    finite(story.atSec, `${path}.stories[${i}].atSec`);
  });
  array(rec.durationCues ?? [], `${path}.durationCues`).forEach((item, i) => {
    const cue = object(item, `${path}.durationCues[${i}]`);
    const island = integer(cue.island, `${path}.durationCues[${i}].island`);
    if (island >= speech.length) fail(`${path}.durationCues[${i}].island`, 'is outside the speech array');
    for (const key of ['atSec', 'endSec', 'statedSec', 'bandMinSec', 'bandMaxSec']) {
      finite(cue[key], `${path}.durationCues[${i}].${key}`);
    }
    string(cue.text, `${path}.durationCues[${i}].text`, true);
    enumValue(cue.precision, `${path}.durationCues[${i}].precision`, ['exact', 'round', 'vague']);
    if (cue.editReview !== undefined) {
      const review = object(cue.editReview, `${path}.durationCues[${i}].editReview`);
      enumValue(review.status, `${path}.durationCues[${i}].editReview.status`, ['approved']);
      string(review.decisionId, `${path}.durationCues[${i}].editReview.decisionId`);
    }
  });
  if (rec.acousticAnalysis !== undefined) acousticAnalysis(rec.acousticAnalysis, `${path}.acousticAnalysis`);
  if (rec.wordlessIslands !== undefined) {
    const indexes = array(rec.wordlessIslands, `${path}.wordlessIslands`);
    const seen = new Set<number>();
    indexes.forEach((value, i) => {
      const index = integer(value, `${path}.wordlessIslands[${i}]`);
      if (index >= speech.length || seen.has(index)) fail(`${path}.wordlessIslands`, 'invalid or duplicate island');
      seen.add(index);
    });
  }
  if (rec.spanVariants !== undefined) {
    const ids = new Set<string>();
    array(rec.spanVariants, `${path}.spanVariants`).forEach((raw, i) => {
      const p = `${path}.spanVariants[${i}]`, variant = object(raw, p);
      const id = string(variant.id, `${p}.id`);
      if (ids.has(id)) fail(`${p}.id`, 'duplicate variant');
      ids.add(id);
      enumValue(variant.role, `${p}.role`, ['opening', 'middle', 'closing']);
      const start = finite(variant.srcStart, `${p}.srcStart`), end = finite(variant.srcEnd, `${p}.srcEnd`);
      if (end <= start || end > nativeSec + .01) fail(p, 'invalid source range');
      const review = object(variant.review, `${p}.review`);
      enumValue(review.status, `${p}.review.status`, ['approved']);
      string(review.decisionId, `${p}.review.decisionId`);
      enumValue(review.by, `${p}.review.by`, ['quorum:cuts-v1', 'ear']);
    });
  }
  if (rec.linkDecisions !== undefined) {
    array(rec.linkDecisions, `${path}.linkDecisions`, speech.length).forEach((raw, i) => {
      const p = `${path}.linkDecisions[${i}]`, d = object(raw, p);
      enumValue(d.schema, `${p}.schema`, ['link-v1']);
      const left = integer(d.afterIsland, `${p}.afterIsland`);
      if (left + 1 >= speech.length) fail(p, 'edge is outside speech');
      for (const key of ['leftEnd', 'rightStart', 'nativeSec']) finite(d[key], `${p}.${key}`);
      enumValue(d.verdict, `${p}.verdict`, ['independent', 'dependent', 'unclear']);
      const review = object(d.review, `${p}.review`);
      enumValue(review.status, `${p}.review.status`, ['approved']);
      enumValue(review.by, `${p}.review.by`, ['quorum:link-v1']);
      string(review.decisionId, `${p}.review.decisionId`);
      for (const key of ['inputSha256', 'rubricSha256', 'manifestSha256']) {
        if (!/^[a-f0-9]{64}$/.test(string(review[key], `${p}.review.${key}`))) fail(p, 'invalid link evidence hash');
      }
      array(review.votes, `${p}.review.votes`, 2).forEach((raw, j) => {
        const vote = object(raw, `${p}.review.votes[${j}]`);
        string(vote.by, `${p}.review.votes[${j}].by`);
        enumValue(vote.verdict, `${p}.review.votes[${j}].verdict`, ['independent', 'dependent', 'unclear']);
      });
    });
  }
  if (rec.toneLevels !== undefined) {
    let previous = -1;
    array(rec.toneLevels, `${path}.toneLevels`).forEach((point, i) => {
      const p = `${path}.toneLevels[${i}]`;
      const values = array(point, p);
      if (values.length !== 3) fail(p, 'must be [source seconds, head RMS, tail RMS]');
      const seconds = finite(values[0], `${p}[0]`);
      if (seconds <= previous) fail(p, 'source seconds must strictly increase');
      previous = seconds;
      for (const column of [1, 2]) {
        if (values[column] !== null) finite(values[column], `${p}[${column}]`);
      }
    });
  }
  if (rec.phaseVariants !== undefined) phaseVariants(rec.phaseVariants, `${path}.phaseVariants`, speech);
  const reach = object(rec.reach, `${path}.reach`);
  const reachMin = finite(reach.minSec, `${path}.reach.minSec`);
  const reachMax = finite(reach.maxSec, `${path}.reach.maxSec`);
  if (reachMax < reachMin) fail(`${path}.reach`, 'has maxSec below minSec');
  return id;
}

/** Validate untrusted cache/network JSON before it becomes application state. */
export function parseManifest(value: unknown): Manifest {
  const doc = object(value, 'manifest');
  const version = integer(doc.version, 'version', 1);
  if (version !== 1) throw new ManifestValidationError(`unsupported manifest version ${version}`);
  const generatedAt = string(doc.generatedAt, 'generatedAt');
  if (!Number.isFinite(Date.parse(generatedAt))) fail('generatedAt', 'must be an ISO date');
  const assetBase = string(doc.assetBase, 'assetBase');
  if (!assetBase.startsWith('/') && !/^https:\/\//.test(assetBase)) fail('assetBase', 'must be a path or HTTPS URL');
  if (assetBase.includes('..') || assetBase.includes('\\')) fail('assetBase', 'must not contain traversal');
  if (doc.releaseDigest !== undefined && !SHA256.test(string(doc.releaseDigest, 'releaseDigest'))) {
    fail('releaseDigest', 'must be a SHA-256 digest');
  }
  if (doc.manifestDigest !== undefined && !SHA256.test(string(doc.manifestDigest, 'manifestDigest'))) {
    fail('manifestDigest', 'must be a SHA-256 digest');
  }
  if (doc.sourceManifestDigest !== undefined
      && !SHA256.test(string(doc.sourceManifestDigest, 'sourceManifestDigest'))) {
    fail('sourceManifestDigest', 'must be a SHA-256 digest');
  }
  if (doc.releaseManifest !== undefined) assetPath(doc.releaseManifest, 'releaseManifest');

  const recordings = array(doc.recordings, 'recordings', MAX_RECORDINGS);
  const ids = new Set<string>();
  recordings.forEach((recording, i) => {
    const id = validateRecording(recording, i);
    if (ids.has(id)) fail(`recordings[${i}].id`, `duplicates ${JSON.stringify(id)}`);
    ids.add(id);
  });
  for (const key of ['themes', 'stories'] as const) {
    array(doc[key] ?? [], key).forEach((item, i) => {
      const entry = object(item, `${key}[${i}]`);
      string(entry.id, `${key}[${i}].id`);
      string(entry.label, `${key}[${i}].label`);
    });
  }

  return { ...doc, themes: doc.themes ?? [], stories: doc.stories ?? [] } as unknown as Manifest;
}

export function parseReleaseDescriptor(value: unknown): ReleaseDescriptor {
  const doc = object(value, 'release');
  if (doc.format !== 'brahm-release/v1') fail('release.format', 'is unsupported');
  const releaseDigest = string(doc.releaseDigest, 'release.releaseDigest');
  const manifestDigest = string(doc.manifestDigest, 'release.manifestDigest');
  const sourceManifestDigest = string(doc.sourceManifestDigest, 'release.sourceManifestDigest');
  if (!SHA256.test(releaseDigest)) fail('release.releaseDigest', 'must be a SHA-256 digest');
  if (!SHA256.test(manifestDigest)) fail('release.manifestDigest', 'must be a SHA-256 digest');
  if (manifestDigest !== releaseDigest) fail('release.manifestDigest', 'must equal releaseDigest');
  if (!SHA256.test(sourceManifestDigest)) fail('release.sourceManifestDigest', 'must be a SHA-256 digest');
  const manifest = object(doc.manifest, 'release.manifest');
  assetPath(manifest.path, 'release.manifest.path');
  const bytes = integer(manifest.bytes, 'release.manifest.bytes', 1);
  const contentDigest = string(manifest.contentDigest, 'release.manifest.contentDigest');
  if (!SHA256.test(contentDigest)) fail('release.manifest.contentDigest', 'must be a SHA-256 digest');
  if (contentDigest !== releaseDigest) fail('release.manifest.contentDigest', 'must equal releaseDigest');
  const hex = releaseDigest.slice('sha256:'.length);
  if (manifest.path !== `releases/${hex}.json`) {
    fail('release.manifest.path', 'must be named by releaseDigest');
  }
  return {
    format: 'brahm-release/v1', releaseDigest, manifestDigest, sourceManifestDigest,
    manifest: { path: manifest.path as string, bytes, contentDigest },
  };
}
